
import React, { useState } from 'react';
import { Globe, Link, ExternalLink, Check, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';

interface DomainSettingsTabProps {
  onSaved: () => Promise<void>;
  initialDomain?: string;
}

const DomainSettingsTab: React.FC<DomainSettingsTabProps> = ({ 
  onSaved,
  initialDomain = ''
}) => {
  const [domain, setDomain] = useState(initialDomain || '');
  const [customDomain, setCustomDomain] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'none' | 'success' | 'error'>('none');
  const { toast } = useToast();
  
  const handleSaveDomain = async () => {
    try {
      // In a real implementation, this would call an API
      toast({
        title: "Domain Updated",
        description: "Your domain settings have been saved",
      });
      await onSaved();
    } catch (error) {
      console.error('Error saving domain:', error);
      toast({
        title: "Error",
        description: "Failed to save domain settings",
        variant: "destructive",
      });
    }
  };
  
  const handleVerifyDomain = async () => {
    if (!customDomain) {
      toast({
        title: "Validation Error",
        description: "Please enter a domain to verify",
        variant: "destructive",
      });
      return;
    }
    
    setIsVerifying(true);
    
    try {
      // Simulate domain verification
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // For demonstration, we'll randomly succeed or fail
      const isSuccess = Math.random() > 0.3;
      
      if (isSuccess) {
        setVerificationStatus('success');
        setDomain(customDomain);
        toast({
          title: "Domain Verified",
          description: `${customDomain} has been verified and is now your primary domain`,
        });
      } else {
        setVerificationStatus('error');
        toast({
          title: "Verification Failed",
          description: "Could not verify domain ownership. Please check DNS settings and try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error verifying domain:', error);
      setVerificationStatus('error');
      toast({
        title: "Verification Error",
        description: "An error occurred while verifying the domain",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Globe className="mr-2 h-5 w-5" />
          Domain Settings
        </CardTitle>
        <CardDescription>
          Configure custom domains for your referral program
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-sm font-medium mb-2">Current Domain</h3>
          {domain ? (
            <div className="flex items-center justify-between p-3 border rounded-md bg-gray-50">
              <div className="flex items-center space-x-2 text-sm">
                <Link className="h-4 w-4 text-primary" />
                <span className="font-mono">{domain}</span>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Primary
                </Badge>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="flex items-center gap-1"
                onClick={() => window.open(`https://${domain}`, '_blank')}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Visit
              </Button>
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic">
              No custom domain configured. Using default subdomain.
            </div>
          )}
        </div>
        
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Add Custom Domain</h3>
          <div className="flex flex-col gap-3">
            <Input
              placeholder="Enter your domain (e.g. referrals.yourbusiness.com)"
              value={customDomain}
              onChange={(e) => {
                setCustomDomain(e.target.value);
                setVerificationStatus('none');
              }}
            />
            
            {verificationStatus === 'success' && (
              <Alert className="bg-green-50 border-green-200">
                <Check className="h-5 w-5 text-green-600" />
                <AlertDescription className="text-green-600">
                  Domain verified successfully.
                </AlertDescription>
              </Alert>
            )}
            
            {verificationStatus === 'error' && (
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <AlertDescription className="text-red-600">
                  Domain verification failed. Please check your DNS settings and try again.
                </AlertDescription>
              </Alert>
            )}
            
            <div className="flex space-x-2">
              <Button 
                onClick={handleVerifyDomain} 
                disabled={isVerifying || !customDomain}
                className="flex-1"
              >
                {isVerifying ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current"></span>
                    Verifying...
                  </>
                ) : "Verify Domain"}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleSaveDomain}
                className="flex-1"
              >
                Save Domain Settings
              </Button>
            </div>
          </div>
        </div>
        
        <div className="space-y-3 mt-6 border-t pt-6">
          <h3 className="text-sm font-medium">DNS Configuration Guide</h3>
          <div className="text-sm space-y-2">
            <p>To verify your domain, add the following DNS records:</p>
            <div className="bg-gray-50 p-3 border rounded-md font-mono text-xs">
              <div className="grid grid-cols-3 gap-2 mb-2 font-semibold text-gray-500">
                <div>Type</div>
                <div>Name</div>
                <div>Value</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>CNAME</div>
                <div>@</div>
                <div>referme.site</div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div>TXT</div>
                <div>_referral-verify</div>
                <div>referme-verification=12345</div>
              </div>
            </div>
            <p className="italic text-gray-500 mt-2">
              DNS changes may take up to 24-48 hours to propagate.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DomainSettingsTab;
